import React, { useEffect, useMemo, useRef } from 'react';
import { Grid, TextField, MenuItem } from '@material-ui/core';
import { FORM_DICTIONARY } from '../schemas/formFields';
import {
    buildPrefillFichaUnicaSalud,
    CAMPOS_PREFILL_FICHA_UNICA_SALUD,
} from '../../helpers/solicitudFichaPrefill';

/* =============================================
    PASO 1: Riesgos y Salud
   ============================================= */
function PasoSaludYRiesgo({ data, onChange, solicitud, onBatchChange }) {
    const solicitudMap = useMemo(() => buildPrefillFichaUnicaSalud(solicitud), [solicitud]);
    const safeValue = (field) => {
        const v = data?.[field]?.valor;
        if (v !== undefined && v !== null && String(v).trim() !== '') {
            return v;
        }
        return solicitudMap[field] ?? '';
    };
    const safeError = (field) => data?.[field]?.error || '';
    const campos_generales = FORM_DICTIONARY.fichas.general;
    const ultimaSolicitudRef = useRef('');

    const obtenerHuellaSolicitud = (sol = {}) => {
        const usuarioId = sol?.usuario_registro?.id || '';
        const solicitudId = sol?.id || '';
        const identificacion = sol?.usuario_registro?.identificacion || '';
        return `${solicitudId}|${usuarioId}|${identificacion}`;
    };

    useEffect(() => {
        if (!solicitud) return;

        const huellaActual = obtenerHuellaSolicitud(solicitud);
        if (ultimaSolicitudRef.current === huellaActual) return;
        ultimaSolicitudRef.current = huellaActual;

        const batch = {};
        CAMPOS_PREFILL_FICHA_UNICA_SALUD.forEach((campo) => {
            const valor = solicitudMap[campo];
            if (valor !== undefined && valor !== null && String(valor).trim() !== '' && !data?.[campo]?.valor) {
                batch[campo] = valor;
            }
        });
        if (Object.keys(batch).length === 0) return;
        if (typeof onBatchChange === 'function') {
            onBatchChange(batch);
        } else {
            Object.entries(batch).forEach(([name, value]) => {
                onChange({ target: { name, value } });
            });
        }
    }, [solicitud, solicitudMap, onBatchChange, onChange]);

    return (
        <Grid container spacing={2}>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    label={campos_generales.nombre_eps.label} 
                    name={campos_generales.nombre_eps.name} 
                    value={safeValue(campos_generales.nombre_eps.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.nombre_eps.name)}
                    helperText={safeError(campos_generales.nombre_eps.name)}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    select 
                    label={campos_generales.sisben.label} 
                    name={campos_generales.sisben.name} 
                    value={safeValue(campos_generales.sisben.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.sisben.name)}
                    helperText={safeError(campos_generales.sisben.name)}
                >
                    <MenuItem value="SI">Sí</MenuItem>
                    <MenuItem value="NO">No</MenuItem>
                </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    label={campos_generales.no_vinculado.label} 
                    name={campos_generales.no_vinculado.name} 
                    value={safeValue(campos_generales.no_vinculado.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.no_vinculado.name)}
                    helperText={safeError(campos_generales.no_vinculado.name)}
                />
            </Grid>


            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    label={campos_generales.grupo_priorizado.label}
                    name={campos_generales.grupo_priorizado.name}
                    value={safeValue(campos_generales.grupo_priorizado.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.grupo_priorizado.name)}
                    helperText={safeError(campos_generales.grupo_priorizado.name)}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    label={campos_generales.se_auto_reconoce_grupo_priorizado.label}
                    name={campos_generales.se_auto_reconoce_grupo_priorizado.name}
                    value={safeValue(campos_generales.se_auto_reconoce_grupo_priorizado.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.se_auto_reconoce_grupo_priorizado.name)}
                    helperText={safeError(campos_generales.se_auto_reconoce_grupo_priorizado.name)}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <TextField 
                    fullWidth 
                    select 
                    label={campos_generales.certificado_grupo_priorizado.label}
                    name={campos_generales.certificado_grupo_priorizado.name}
                    value={safeValue(campos_generales.certificado_grupo_priorizado.name)} 
                    onChange={onChange}
                    required
                    error={!!safeError(campos_generales.certificado_grupo_priorizado.name)}
                    helperText={safeError(campos_generales.certificado_grupo_priorizado.name)}
                >
                    <MenuItem value="SI">Sí</MenuItem>
                    <MenuItem value="NO">No</MenuItem>
                </TextField>
            </Grid>

            {[
                { label: campos_generales.ideacion_suicida.label, name: campos_generales.ideacion_suicida.name, required: true },
                { label: campos_generales.intento_suicida.label, name: campos_generales.intento_suicida.name, required: true },
                { label: campos_generales.conducta_suicida.label, name: campos_generales.conducta_suicida.name, required: true },
                { label: campos_generales.deseo_muerte.label, name: campos_generales.deseo_muerte.name, required: true },
                { label: campos_generales.deseo_danarse.label, name: campos_generales.deseo_danarse.name, required: true },
                { label: campos_generales.plan_suicida.label, name: campos_generales.plan_suicida.name, required: true },
                { label: campos_generales.trataste_suicidarte.label, name: campos_generales.trataste_suicidarte.name, required: true }
            ].map((preg) => (
                <Grid item xs={12} md={4} key={preg.name}>
                    <TextField 
                        fullWidth 
                        select 
                        label={preg.label} 
                        name={preg.name} 
                        value={safeValue(preg.name)} 
                        onChange={onChange}
                        required={preg.required}
                        error={!!safeError(preg.name)}
                        helperText={safeError(preg.name)}
                    >
                        <MenuItem value="SI">Sí</MenuItem>
                        <MenuItem value="NO">No</MenuItem>
                    </TextField>
                </Grid>
            ))}
        </Grid>
    );
}

/* =============================================
    PASO 2: Descripción de la Sesión
   ============================================= */
function PasoDescripcionUnicaSesion({ data, onChange, solicitud }) {
    const safeValue = (field) => (data?.[field]?.valor || '');
    const safeError = (field) => data?.[field]?.error || '';
    const campos = FORM_DICTIONARY.fichas.unica_sesion;

    const CAMPOS_DESCRIPCION = [
        { label: campos.descripcion_problematica.label, name: campos.descripcion_problematica.name, required: true },
        { label: campos.motivo_consulta.label, name: campos.motivo_consulta.name, required: true },
        { label: campos.resumen_procedimiento.label, name: campos.resumen_procedimiento.name, required: true },
        { label: campos.observacion_recomendaciones.label, name: campos.observacion_recomendaciones.name, required: true },
        ];

    return (
        <Grid container spacing={2}>
            {CAMPOS_DESCRIPCION.map(campo => (
                <Grid item xs={12} key={campo.name}>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={campo.label}
                    name={campo.name}
                    value={safeValue(campo.name)}
                    onChange={onChange}
                    required={campo.required}
                    error={!!safeError(campo.name)}
                    helperText={safeError(campo.name)}
                />
                </Grid>
            ))}
        </Grid>
    );
}


/* =============================================
    EXPORTACIÓN DE PASOS
   ============================================= */
export const StepsUnicaSesion = [
    { label: 'Riesgos y Salud', render: (props) => <PasoSaludYRiesgo {...props} /> },
    { label: 'Descripción', render: (props) => <PasoDescripcionUnicaSesion {...props} /> },
];
